import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useStaffNames } from "@/lib/staff";

const TURLER = ["Şahıs", "Limited", "Anonim", "Diğer"];
const BEYAN = ["KDV1", "KDV2", "MUHSGK", "Damga", "Geçici Vergi", "Gelir Vergisi", "Kurumlar"];
const EMPTY = { beyanname_turleri: ["KDV1", "MUHSGK"] };

export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const editId = client?.id || null;
  const staffNames = useStaffNames();

  useEffect(() => {
    if (open) setForm(client ? { ...client, beyanname_turleri: client.beyanname_turleri || [] } : EMPTY);
  }, [open, client]);

  const toggleBeyan = (b) => {
    const cur = form.beyanname_turleri || [];
    setForm({ ...form, beyanname_turleri: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] });
  };

  const save = async () => {
    if (!form.unvan) return toast.error("Ünvan zorunlu");
    try {
      const payload = { ...form, aylik_ucret: Number(form.aylik_ucret) || 0 };
      let saved;
      if (editId) {
        saved = (await api.put(`/clients/${editId}`, payload)).data;
        toast.success("Mükellef güncellendi");
      } else {
        saved = (await api.post("/clients", payload)).data;
        toast.success("Mükellef eklendi");
      }
      onOpenChange(false);
      onSaved && onSaved(saved);
    } catch (e) { toast.error(e.response?.data?.detail || "Kayıt başarısız"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? "Mükellef Düzenle" : "Yeni Mükellef"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {[["unvan", "Ünvan *"], ["vkn", "Vergi Kimlik No"], ["tckn", "TC Kimlik No"], ["vergi_dairesi", "Vergi Dairesi"],
            ["nace", "NACE Kodu"], ["faaliyet", "Faaliyet Konusu"], ["telefon", "Telefon"], ["email", "E-posta"],
            ["yetkili", "Yetkili Kişi"], ["kep", "KEP Adresi"], ["sorumlu_personel", "Sorumlu Personel"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{l}</Label>
              <Input data-testid={`client-field-${k}`} list={k === "sorumlu_personel" ? "staff-names-form" : undefined} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <datalist id="staff-names-form">{staffNames.map((n) => <option key={n} value={n} />)}</datalist>
          <div className="space-y-1.5">
            <Label className="text-xs">Şirket Türü</Label>
            <Select value={form.sirket_turu} onValueChange={(v) => setForm({ ...form, sirket_turu: v })}>
              <SelectTrigger data-testid="client-field-tur"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>{TURLER.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Aylık Ücret (TL)</Label>
            <Input data-testid="client-field-ucret" type="number" value={form.aylik_ucret || ""} onChange={(e) => setForm({ ...form, aylik_ucret: e.target.value })} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Terk / Ayrılış Tarihi</Label>
            <Input
              data-testid="client-field-ayrilis-tarihi"
              type="date"
              value={form.ayrilis_tarihi || ""}
              onChange={(e) => setForm({ ...form, ayrilis_tarihi: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Ayrılış Nedeni</Label>
            <Select
              value={form.ayrilis_nedeni || ""}
              onValueChange={(v) => setForm({ ...form, ayrilis_nedeni: v })}
            >
              <SelectTrigger data-testid="client-field-ayrilis-nedeni">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Faaliyet Terk">Faaliyet Terk</SelectItem>
                <SelectItem value="Başka Meslek Mensubuna Geçiş">Başka Meslek Mensubuna Geçiş</SelectItem>
                <SelectItem value="Diğer">Diğer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Beyanname Türleri</Label>
          <div className="flex flex-wrap gap-3">
            {BEYAN.map((b) => (
              <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={(form.beyanname_turleri || []).includes(b)} onCheckedChange={() => toggleBeyan(b)} /> {b}
              </label>
            ))}
          </div>
          {(form.beyanname_turleri || []).includes("MUHSGK") && (
            <div className="flex items-center gap-2 mt-2">
              <Label className="text-xs">MUHSGK Dönemi</Label>
              <select data-testid="client-field-muhsgk-donemi" value={form.muhsgk_donemi || "Aylık"}
                onChange={(e) => setForm({ ...form, muhsgk_donemi: e.target.value })}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="Aylık">Aylık</option>
                <option value="3 Aylık">3 Aylık</option>
              </select>
              {form.muhsgk_donemi === "3 Aylık" && <span className="text-xs text-muted-foreground">Mart, Haziran, Eylül, Aralık dışındaki aylarda MUAF</span>}
            </div>
          )}
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.edefter} onCheckedChange={(v) => setForm({ ...form, edefter: v })} /> e-Defter</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.efatura} onCheckedChange={(v) => setForm({ ...form, efatura: v })} /> e-Fatura</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button data-testid="save-client-btn" onClick={save}>{editId ? "Güncelle" : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
